// サイト管理用のカスタムフック
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SiteService } from '../services/siteService';
import { DomainService } from '../services/domainService';
import { LandingPage } from '../types';

export const useSites = () => {
  const { currentUser } = useAuth();
  const [sites, setSites] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // サイト一覧取得
  const fetchSites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        // ユーザーが未ログインの場合は空の配列を返す
        setSites([]);
        return;
      }
      
      const sitesData = await SiteService.getSites();
      setSites(sitesData);
    } catch (err) {
      console.error('サイト取得エラー:', err);
      setError(err instanceof Error ? err.message : 'サイトの取得に失敗しました');
      // エラーが発生しても空の配列を設定してローディングを終了
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  // サイト作成
  const createSite = async (siteData: Omit<LandingPage, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser) throw new Error('ログインが必要です');
    
    try {
      const siteId = await SiteService.createSite({
        ...siteData,
        createdByUserId: currentUser.id,
        updatedByUserId: currentUser.id,
      });
    
    // ドメイン設定はユーザーがサイト設定画面で明示的に行う
    // （Cloudflare Zone Management方式では、ユーザーが独自ドメインを入力して設定する）
    
    // ローカル状態を更新
      const newSite: LandingPage = {
        ...siteData,
        id: siteId,
        createdByUserId: currentUser.id,
        updatedByUserId: currentUser.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setSites(prev => [newSite, ...prev]);
      
      return siteId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サイトの作成に失敗しました');
      throw err;
    }
  };

  // サイト更新
  const updateSite = async (siteId: string, siteData: Partial<LandingPage>) => {
    if (!currentUser) throw new Error('ログインが必要です');
    
    try {
      await SiteService.updateSite(siteId, {
        ...siteData,
        updatedByUserId: currentUser.id,
      });
      
      // ローカル状態を更新
      setSites(prev => prev.map(site => 
        site.id === siteId 
          ? { ...site, ...siteData, updatedByUserId: currentUser.id, updatedAt: new Date() }
          : site
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サイトの更新に失敗しました');
      throw err;
    }
  };

  // サイト削除
  const deleteSite = async (siteId: string) => {
    try {
      await SiteService.deleteSite(siteId);
      
      // ローカル状態を更新
      setSites(prev => prev.filter(site => site.id !== siteId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サイトの削除に失敗しました');
      throw err;
    }
  };

  // サイト公開
  const publishSite = async (siteId: string, customDomain?: string) => {
    try {
      const publishedUrl = await SiteService.publishSite(siteId, customDomain);
      
      // ローカル状態を更新
      setSites(prev => prev.map(site => 
        site.id === siteId 
          ? { ...site, status: 'published', publishedUrl, updatedAt: new Date() }
          : site
      ));
      
      return publishedUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サイトの公開に失敗しました');
      throw err;
    }
  };

  // サイト非公開
  const unpublishSite = async (siteId: string) => {
    try {
      await SiteService.unpublishSite(siteId);
      
      // ローカル状態を更新
      setSites(prev => prev.map(site => 
        site.id === siteId 
          ? { ...site, status: 'unpublished', publishedUrl: undefined, updatedAt: new Date() }
          : site
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サイトの非公開に失敗しました');
      throw err;
    }
  };

  useEffect(() => {
    fetchSites();
  }, [currentUser]);

  return {
    sites,
    loading,
    error,
    fetchSites,
    createSite,
    updateSite,
    deleteSite,
    publishSite,
    unpublishSite,
  };
};
